// lib/supabase.ts
/**
 * Supabase client configuration
 * Handles authentication and session management
 */

import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { createClient } from '@supabase/supabase-js'

// Client-side Supabase client (for use in components)
export const supabase = createClientComponentClient({
  supabaseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL!,
  supabaseKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
})

// Server-side Supabase client (for use in server components/API routes)
export const supabaseAdmin = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.SUPABASE_SERVICE_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  }
)

// Auth helpers
export const auth = {
  // Sign up new user
  signUp: async (email: string, password: string, metadata?: Record<string, any>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    })

    if (error) throw error
    return data
  },

  // Sign in existing user
  signIn: async (email: string, password: string) => {
    console.log('[supabase.auth.signIn] Calling signInWithPassword...')
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    console.log('[supabase.auth.signIn] Result:', {
      session: data.session ? 'exists' : 'null',
      user: data.user?.email,
      error: error?.message
    })

    if (error) throw error
    return data
  },

  // Sign in with OAuth (Google, GitHub, etc.)
  signInWithOAuth: async (provider: 'google' | 'github') => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/auth/callback`,
      },
    })

    if (error) throw error
    return data
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get current session
  getSession: async () => {
    const { data, error } = await supabase.auth.getSession()
    console.log('[supabase.auth.getSession] Result:', {
      hasSession: !!data.session,
      user: data.session?.user?.email,
      error: error?.message
    })
    if (error) throw error
    return data.session
  },

  // Get current user
  getUser: async () => {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    return data.user
  },

  // Reset password
  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_APP_URL}/auth/reset-password`,
    })

    if (error) throw error
  },

  // Update password
  updatePassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) throw error
  },
}

// Session helpers
export const session = {
  // Check if user is authenticated
  isAuthenticated: async () => {
    const session = await auth.getSession()
    return !!session
  },

  // Get access token
  getAccessToken: async () => {
    const session = await auth.getSession()
    return session?.access_token
  },

  // Refresh session
  refresh: async () => {
    const { data, error } = await supabase.auth.refreshSession()
    if (error) throw error
    return data.session
  },
}

// Auth state change listener
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  return supabase.auth.onAuthStateChange(callback)
}
